import {LabelStore} from '../base/label-store';
import {ApiRepository} from '../base/api-repository';
import {action, makeObservable, observable} from 'mobx';
import {todayAsISOString} from '../base/dateUtils';
import {ViewStore} from '../base/view-store';

export class StationStore extends ViewStore {
    @observable
    payload?: StationPayload = undefined;
    @observable
    variables: StationVariables = new StationVariables();

    constructor(private api: ApiRepository, labelStore: LabelStore) {
        super(labelStore);
        makeObservable(this);
    }

    @action
    onEnter(businessUnitId: string): void {
        this.load(async () => {
            this.payload = await this.loadStationQuestion(businessUnitId);
            if (this.payload.translatableQuestionId !== undefined) {
                this.preparePayloadAction();
                this.variables.hourlyRefreshIntervalId = setInterval(() => {
                    this.refreshPayload();
                }, 1000 * 60 * 60);
            } else {
                this.addBotMessage(this.label('station.no-data'));
            }
        },
        );
    }

    @action
    onExit(): void {
        const hourlyRefreshIntervalId = this.variables.hourlyRefreshIntervalId;
        const questionResetId = this.variables.questionResetId;

        if (hourlyRefreshIntervalId !== undefined) {
            clearInterval(hourlyRefreshIntervalId);
        }
        if (questionResetId !== undefined) {
            clearTimeout(questionResetId);
        }
    }

    @action
    onSubmitChoice(value: string): void {
        const type = this.variables.action!.actionType;

        if (type === 'ASK_STATION_QUESTION') {
            this.onSubmitAnswer(value as '5' | '1');
        } else {
            this.onAddFinish(value as 'yes');
        }
    }

    @action
    async onSubmitAnswer(answer: '5' | '1'): Promise<void> {
        const questionResetId = this.variables.questionResetId;

        if (questionResetId !== undefined) {
            clearTimeout(questionResetId);
        }

        this.addEmployeeMessage(this.label(`chat.binary.${answer}`), answer === '5' ? 'green' : 'red');
        this.variables.action = undefined;
        await this.submitStationAnswer(answer);
        this.addBotMessage(this.label('station.goodbye'));
        this.addFinish();
        this.variables.questionResetId = setTimeout(() => {
            this.addFinish();
            this.preparePayloadAction();
        }, 1000 * 5);
    }

    @action
    async onAddFinish(value: 'yes'): Promise<void> {
        const chatAction = this.variables.action! as MultipleChoiceAction;
        const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

        this.addEmployeeMessage(selectedChoice.text, 'green');
        this.preparePayloadAction();
    }

    private loadStationQuestion(businessUnitId: string): Promise<StationPayload> {
        return this.api.get<StationPayload>(`/station/${businessUnitId}`);
    }

    private preparePayloadAction(): void {
        this.clearStationChat();
        this.addBotMessage(this.payload!.translatableQuestionText!);
        this.addQuestionAction();
    }

    private addBotMessage(text: string, date: string = todayAsISOString()): void {
        this.variables.preparedMessages.push({text, party: 'bot', date, color: 'white'});
    }

    private addEmployeeMessage(text: string, color: 'red' | 'green' | 'white' = 'white', date: string = todayAsISOString()): void {
        this.variables.preparedMessages.push({text, party: 'employee', date, color});
    }

    private addQuestionAction(): void {
        const type = 'ASK_STATION_QUESTION';
        const choices: ActionChoice[] = [
            {text: this.label('chat.binary.5'), value: '5', color: 'green'},
            {text: this.label('chat.binary.1'), value: '1', color: 'red'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private submitStationAnswer(answer: '5' | '1'): Promise<void> {
        const stationAnswer: StationAnswerToSave = {
            translatableQuestionId: this.payload!.translatableQuestionId!,
            questionType: this.payload!.questionType!,
            answer,
        };

        return this.api.post<void>(`/station/business-unit/${this.payload!.businessUnitId}/station-answer`, stationAnswer);
    }

    private clearStationChat(): void {
        this.variables.preparedMessages = [];
        this.variables.action = undefined;
    }

    private addFinish(): void {
        const type = undefined;
        const choices: ActionChoice[] = [
            {text: this.label('station.close'), value: 'yes', color: 'green'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private async refreshPayload(): Promise<StationPayload> {
        return this.api.get(`/station/${this.payload!.businessUnitId}`);
    }
}

interface StationPayload {
    language: 'en' | 'et' | 'ru';
    businessUnitId: string;
    companyId: string;
    actionType?: ActionType;
    translatableQuestionText?: string;
    translatableQuestionId?: string;
    questionType?: QuestionType;
}

export interface StationAnswerToSave {
    translatableQuestionId: string;
    questionType: QuestionType;
    answer: '5' | '1';
}

class StationVariables {
    @observable
    preparedMessages: StationMessage[];
    @observable
    action?: StationChatAction;
    @observable
    hourlyRefreshIntervalId?: NodeJS.Timeout = undefined;
    @observable
    questionResetId?: NodeJS.Timeout = undefined;

    constructor(preparedMessages: StationMessage[] = [], chatAction?: StationChatAction) {
        this.preparedMessages = preparedMessages;
        this.action = chatAction;
        makeObservable(this);
    }
}

export class StationMessage {
    text: string;
    party: 'employee' | 'bot';
    date: string;
    color: 'red' | 'green' | 'white';

    constructor(text: string, party: 'employee' | 'bot', date: string, color: 'red' | 'green' | 'white') {
        this.text = text;
        this.party = party;
        this.date = date;
        this.color = color;
    }
}

type ActionType = 'ASK_STATION_QUESTION' | undefined;

type QuestionType = 'CATALOG_QUESTION' | 'ADHOC_QUESTION';

export interface StationChatAction {
    actionType: ActionType;
}

export class MultipleChoiceAction implements StationChatAction {
    choices: ActionChoice[];
    actionType: ActionType;

    constructor(choices: ActionChoice[], type: ActionType) {
        makeObservable(this);
        this.choices = choices;
        this.actionType = type;
    }
}

class ActionChoice {
    text: string;
    value: string;
    color: 'green' | 'red';

    constructor(text: string, value: string, color: 'green' | 'red') {
        this.text = text;
        this.value = value;
        this.color = color;
    }
}

